import { createAsyncThunk } from '@reduxjs/toolkit'
import { REQUEST_METHOD, apiDELETE, apiGET, apiPOST, apiPUT, apiPATCH } from 'utils/apiClient'
import { sendErrorNotification, sendSuccessNotification } from './notifications'

interface AsyncThunkConfig {
  rejectValue: APIError
}

interface APIError {
  message: string
}

interface APIFunctionArgs {
  url: string
  data?: any
  params?: any
  contentType?: string
}

interface APIFunction {
  (args: APIFunctionArgs): Promise<any>
}

interface CreateApiThunkParams<ReturnType, PayloadType> {
  action: string
  /** Untyped axios path. Omit when passing `request`. */
  method?: REQUEST_METHOD
  responseKey?: string
  contentType?: string
  /** Untyped axios path. Omit when passing `request`. */
  formatUrl?: (payload: PayloadType) => string
  /**
   * Typed path: a function from src/api that already returns the response body,
   * checked against the generated schema. Takes precedence over method/formatUrl,
   * and skips responseKey — the api module owns response unwrapping.
   */
  request?: (payload: PayloadType) => Promise<ReturnType>
  getData?: (payload: PayloadType) => any
  getParams?: (payload: PayloadType) => any
  getSuccessMessage?: (payload: PayloadType) => string | undefined
  onSuccess?: (payload: PayloadType, response: ReturnType, thunkApi: any) => void
  onFailure?: (payload: PayloadType, thunkApi: any, error: any) => void
}

const apiFunctionMap: Record<REQUEST_METHOD, APIFunction> = {
  GET: apiGET,
  PUT: apiPUT,
  POST: apiPOST,
  DELETE: apiDELETE,
  PATCH: apiPATCH,
}

export function createApiThunk<ReturnType, PayloadType>({
  action,
  method,
  responseKey,
  contentType,
  formatUrl,
  request,
  getData,
  getParams,
  getSuccessMessage,
  onSuccess,
  onFailure,
}: CreateApiThunkParams<ReturnType, PayloadType>) {
  return createAsyncThunk<ReturnType, PayloadType, AsyncThunkConfig>(action, async (payload, thunkApi) => {
    try {
      let response: any

      if (request) {
        response = await request(payload)
      } else {
        if (!method || !formatUrl) {
          throw new Error(`createApiThunk(${action}): pass either request, or both method and formatUrl`)
        }

        const apiFunction = apiFunctionMap[method]

        const res = await apiFunction({
          url: formatUrl(payload),
          contentType,
          data: getData ? getData(payload) : undefined,
          params: getParams ? getParams(payload) : undefined,
        })

        response = responseKey ? res.data[responseKey] : res.data
      }

      if (contentType === 'multipart/form-data') {
        try {
          const text = await response.text()
          response = JSON.parse(text)
        } catch (error) {
          // do nothing, use existing response
        }
      }

      if (onSuccess) {
        onSuccess(payload, response, thunkApi)
      }

      const successMessage = getSuccessMessage ? getSuccessMessage(payload) : undefined
      if (successMessage) {
        sendSuccessNotification(successMessage)
      }

      return response
    } catch (error: any) {
      if (onFailure) {
        onFailure(payload, thunkApi, error)
      }

      const message = error.message ?? 'Something went wrong. Please try again.'
      sendErrorNotification(message)

      return thunkApi.rejectWithValue(error.message ?? { message: 'Unknown error' })
    }
  })
}
